<template>
    <div class="fixed top-0 w-full bg-echoes-background-black opacity-100 z-30 transition-transform duration-300 ease-in-out" 
         :class="{ 'navbar-hidden': !isNavbarVisible, 'navbar-visible': isNavbarVisible }">
         <div class="flex items-center justify-between pt-3 pb-1 px-4">
            <div>
                <img src="../../assets/images/logo-dark.png" class="h-5" alt="Portex logo" loading="lazy">
                
            </div>
            <div class="flex items-center justify-center gap-2">
                <div class="col-span-1 flex items-center justify-center relative">
                    <LoginIcon v-if="!verifyUserInfo()" class="w-7 h-7" @click="router.push('/login')" />
                    <LogoutIcon v-else class="w-7 h-7 text-echoes-primary" @click="logout" />
                </div>
                <div class="col-span-1 flex items-center justify-center relative" v-if="verifyUserInfo()">
                    <NotificationsBell @notificationsToggled="handleNotificationsToggle" />
                </div>

            </div>
         </div>
        <div class="flex py-2 px-3 gap-2 w-full">
            <TextInput v-model="searchQuery" id="navbar-search"
                placeholder="Search all datasets, modalities, use cases..."
                class="w-full navbar-search-input" @input="handleSearch">
                <template #icon>
                    <i class="uil uil-search text-sm"></i>
                </template>
                <template #right-icon v-if="searchQuery">
                    <i class="uil uil-times cursor-pointer text-sm" @click="clearSearch"></i>
                </template>
            </TextInput>
        </div>
    </div>
    
    <div :class="{ 'blur-content': notificationsOpen }" class="pt-24 pb-20">
        <slot></slot>
    </div>

    <div class="fixed bottom-0 w-full z-30 rounded-t-xl border-gray-200 flex justify-around" v-if="verifyUserInfo()">
        <div class="flex flex-col items-center w-full transition-colors duration-500 ease-in-out" 
            v-for="tab in tabs" :key="tab.name" 
            :class="{ 
                'bg-echoes-primary rounded-t-lg': tabActive === tab.name,
                'bg-echoes-background-gray mt-2 py-1 text-echoes-primary': tabActive !== tab.name
            }"
            @click="tabActive = tab.name; router.push(tab.path)">
                <i :class="tab.icon" class="text-2xl"></i>
                <span class="text-xs tracking-[0.2em] pb-1" v-if="tabActive === tab.name">{{ tab.title }}</span>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import TextInput from '@/components/forms/inputs/text-input.vue';
import { useRouter } from 'vue-router';
import NotificationsBell from './notification-bell.vue'
import { useExplorerStore } from '@/store/ExplorerStore';
import { useStorage } from '@/composables/use-storage.js';
import LoginIcon from '@/icons/login.vue';
import LogoutIcon from '@/icons/logout.vue';
import axios from '@/plugins/axios';

const { verifyUserInfo, clearUserData, userAuthToken } = useStorage();
const authToken = ref(userAuthToken.value);

const router = useRouter();
const explorerStore = useExplorerStore();

const tabs = [
    {
        name: 'explore',
        icon: 'uil uil-home',
        title: 'Explore',
        path: '/explore',
    },
    {
        name: 'creator-profile',
        icon: 'uil uil-globe',    
        title: 'Data Studio',
        path: '/creator-profile',
    },
    {
        name: 'creator-profile-edit',
        icon: 'uil uil-setting',
        title: 'Settings',
        path: '/creator-profile-edit',
    },
]
const tabActive = ref('home');

const searchQuery = ref('');
const lastSearchTerm = ref('');

// Navbar scroll behavior
const isNavbarVisible = ref(true);
const lastScrollY = ref(0);
const scrollThreshold = 10; // Threshold to avoid too sensitive scroll detection

const handleSearch = () => {
    debouncedSearch(searchQuery.value);
};

const clearSearch = () => {
    searchQuery.value = '';
    lastSearchTerm.value = '';
    explorerStore.setSearchQuery('');

    
    if (router.currentRoute.value.path === '/explore') {
        const query = { ...router.currentRoute.value.query };
        delete query.search;
        router.replace({ query });
    }
};

const notificationsOpen = ref(false);

const handleNotificationsToggle = (isOpen) => {
    notificationsOpen.value = isOpen;
}

const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    }
};

const filterItems = (value) => {
    if (!value || !value.trim()) {
        explorerStore.setSearchQuery('');
    
        if (router.currentRoute.value.path === '/explore') {
            const query = { ...router.currentRoute.value.query };
            delete query.search;
            router.replace({ query });
        }
        return;
    }

    const searchValue = value.trim();
    explorerStore.setSearchQuery(searchValue);

    if (router.currentRoute.value.path !== '/explore') {
        router.push({ path: '/explore', query: { search: searchValue } });
    } else {
        const query = { ...router.currentRoute.value.query, search: searchValue };
        router.replace({ query });
    }
};

const debouncedSearch = debounce((value) => {
    if (value === lastSearchTerm.value) return;
    if (value.length === 0 || value.length >= 2) {
        lastSearchTerm.value = value;
        filterItems(value);
    }
}, 100);

// Handle navbar visibility on scroll
const handleScroll = () => {
    const currentScrollY = window.scrollY;
    
    // Don't do anything if scroll distance is too small
    if (Math.abs(currentScrollY - lastScrollY.value) < scrollThreshold) {
        return;
    }
    
    // Show navbar when scrolling up or at the top
    if (currentScrollY < lastScrollY.value || currentScrollY <= 0) {
        isNavbarVisible.value = true;
    } 
    // Hide navbar when scrolling down (but not immediately at the top)
    else if (currentScrollY > lastScrollY.value && currentScrollY > 80) {
        isNavbarVisible.value = false;
    }
    
    lastScrollY.value = currentScrollY;
};

watch(() => searchQuery.value, (newSearchQuery) => {
    if (searchQuery.value === '/explore') {
        const searchValue = newSearchQuery || '';
        if (searchQuery.value !== searchValue) {
            // searchQuery.value = searchValue;?
            lastSearchTerm.value = searchValue;
        }
    }
}, { immediate: true });

onMounted(() => {
    tabActive.value = router.currentRoute.value.name;
    
    if (router.currentRoute.value.path === '/explore' && router.currentRoute.value.query.search) {
        searchQuery.value = router.currentRoute.value.query.search;
        lastSearchTerm.value = router.currentRoute.value.query.search;
        explorerStore.setSearchQuery(router.currentRoute.value.query.search);
    }
    
    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    lastScrollY.value = window.scrollY;
});

onUnmounted(() => {
    // Remove scroll event listener
    window.removeEventListener('scroll', handleScroll);
});

const logout = async () => {
    try {
        clearUserData();
        const response = await axios.post('/api/logout', {
            headers: {
                'Authorization': `Bearer ${authToken.value}`
            }
        });

        if (response.status === 200) {
            router.push('/login');
        }
    } catch (error) {
        console.error("Error logging out:", error);
    }
};
</script>

<style scoped lang="postcss">
.blur-content {
    @apply blur-lg pointer-events-none transition-all duration-300
}

.navbar-visible {
    transform: translateY(0);
}

.navbar-hidden {
    transform: translateY(-100%);
}
</style>